import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary } from "@mui/material";
import { isEmpty, uniqueId } from "lodash";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { sourceList } from "utils/CommonConstants";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import Typography from "../Typography";
import { fetchFilterSelectedPlansAsync, fetchmembersInfo } from "./Filters.slice";
import { getDynamicFiltersOptions } from "./FiltersFunctions";
import { isSelectAllWithEmptyOptions, optionsTypes } from "./FiltersHelper";

const GlobalFilterDescription = (props: any) => {
  const dispatch = useAppDispatch();
  const [filterList, setFilterList] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [listShowMore, setListShowMore] = useState<any>({});
  const { filterCategories, planInfo } = useAppSelector((state) => state.filters);
  const ownerInfo = useAppSelector((state) => state.filters.ownerInfo);
  const filterCategoryData = useAppSelector((state) => state.filters.filterCategoryData);
  const memberInfo = useAppSelector((state) => state.filters.memberInfo);
  const staticPropertyInfo = useAppSelector((state) => state.filters.staticPropertyInfo);
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[props.page]);
  const filterRequestPayload = props?.isGlobalCheckBoxApplicable
    ? globalFiltersforPage?.isFilterApplied
      ? globalFiltersforPage?.modifiedFilter
      : globalFiltersforPage?.defaultFilter
    : (isEmpty(globalFiltersforPage?.modifiedFilter)
        ? globalFiltersforPage?.defaultFilter
        : globalFiltersforPage?.modifiedFilter) || [];

  useEffect(() => {
    const fetchData = async () => {
      const resolvedList = await Promise.all(
        filterRequestPayload?.map(async (item: any) => {
          if (isSelectAllWithEmptyOptions(item)) return { ...item, options: [] };
          try {
            const optionRes = await getFilterOptions(item?.key, item?.options, item?.id);
            return {
              ...item,
              options: optionRes
            };
          } catch (error) {
            return {
              ...item,
              options: []
            };
          }
        })
      );
      setFilterList(resolvedList);
    };
    fetchData();
  }, [filterRequestPayload]);

  const getFilterOptions = async (key: string, options: string[], id: number) => {
    let optionsRes: string[] = [];
    switch (key) {
      case "planOwner": {
        const filtered = options?.filter((item: string) => ownerInfo?.findIndex((prop: any) => prop.id === item) < 0);
        let ownerData = ownerInfo;
        if (filtered?.length) {
          const props: any = {
            hltIds: filtered,
            page: 0,
            size: filtered?.length,
            type: "owner"
          };
          const data: any = await dispatch(fetchmembersInfo(props));
          ownerData = [
            ...ownerData,
            ...(data.payload?.users?.map((item: any) => {
              return {
                id: item.hltId,
                name: getUserNameWithConditionalCheck(item?.displayName, item?.firstName, item?.lastName)
              };
            }) ?? [])
          ];
        }
        optionsRes = options?.map((item: any) => ownerData?.find((item2: any) => item2?.id === item)?.name);
        break;
      }
      case "users":
      case "hltIds":
      case "searchedHltId":
      case "hltId":
      case "assignedBy": {
        optionsRes = options?.map((item: any) => {
          const user = memberInfo?.find((item2: any) => item2?.hltId === item);
          return getUserNameWithConditionalCheck(user?.displayName, user?.firstName, user?.lastName);
        });
        break;
      }
      case "planCmsId":
      case "ids":
      case "cmsId": {
        const filtered = options?.filter((item: string) => planInfo?.findIndex((prop: any) => prop.id === item) < 0);
        let plansData = planInfo;
        if (filtered?.length) {
          const data: any = await dispatch(
            fetchFilterSelectedPlansAsync({
              payload: {
                filter: {
                  ids: filtered
                }
              }
            })
          );
          plansData = [...plansData, ...(data?.payload?.plans ?? [])];
        }
        optionsRes = resolvePlanName(options, plansData);
        break;
      }
      case "staticListIds": {
        optionsRes = options?.map((item: any) => {
          const property = staticPropertyInfo?.find((item2: any) => item2?.id === item);
          return property?.name;
        });
        break;
      }
      default: {
        const selectedFilterPanel = filterCategories?.find((fil: any) => fil?.id === id);
        if (selectedFilterPanel?.optionsType === "key-array-value-array")
          optionsRes = sourceList
            ?.filter((item: any) => options?.includes(item.value[0]))
            ?.map((val: any) => val?.name);
        else if (selectedFilterPanel?.optionsType === "2-level-nested-filter-categories")
          optionsRes = options?.map(
            (option: any) => filterCategoryData?.find((item: any) => item?.id === option)?.name
          );
        else if (selectedFilterPanel?.optionsType === "key-value-array-local")
          optionsRes = options?.map((item: any) => {
            const selectedItem = getDynamicFiltersOptions(selectedFilterPanel.key)?.find(
              (item2: any) => item2?.key === item
            ).value;
            return selectedItem;
          });
        else
          optionsRes = !["key-value-array", optionsTypes["static-single-select-key-value"]].includes(
            selectedFilterPanel?.optionsType
          )
            ? options
            : options?.map((item: any) => selectedFilterPanel?.filters?.find((val: any) => val?.key === item)?.value);
      }
    }
    return optionsRes;
  };

  const resolvePlanName = (options: string[], planInfo: any) => {
    return options?.map((item: any) => planInfo?.find((planValue: any) => planValue?.id === item)?.name);
  };

  const handleChange = () => {
    setExpanded(!expanded);
  };

  const getSliceNoToShowRecord = (item: any) => {
    return listShowMore[item.key] || 10;
  };

  const handleShowMoreClick = (itemkey: string, length: number) => {
    setListShowMore({ ...listShowMore, [itemkey]: length });
  };

  const getMoreOrLessButton = (label: string, item: any, isSeeLess: boolean) => {
    return (
      <Typography
        showBold
        sx={{ textDecoration: "underline", cursor: "pointer" }}
        color={tokens.Text.General.Color}
        variant={"label"}
        onClick={(event: any) => {
          event.stopPropagation();
          handleShowMoreClick(item.key, isSeeLess ? 10 : item?.options?.length);
        }}
      >
        {label}
      </Typography>
    );
  };

  return (
    <>
      {filterRequestPayload?.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={handleChange}
          sx={{
            backgroundColor: props?.bgColor ?? tokens.Background.General.Secondary,
            borderRadius: "8px",
            marginY: "16px",
            boxShadow: "none",
            border: "none",
            "&::before": {
              display: "none"
            }
          }}
        >
          <AccordionSummary
            sx={{ ".MuiAccordionSummary-content": { width: "100%", paddingX: "2px" } }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            {!expanded ? (
              <div style={{ width: "100%" }}>
                <Typography
                  sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
                  className="ellipse-one"
                  variant={"label"}
                  color={tokens.Text.General.Secondary}
                >
                  {filterList.map((item: any, index: number) => {
                    return (
                      <div key={index}>
                        <span style={{ marginRight: "1px", fontWeight: "700" }}>{item?.name}</span>
                        <span style={{ marginRight: "4px" }}>
                          ({item?.options?.length ? item?.options?.length : "All"})
                        </span>
                      </div>
                    );
                  })}
                </Typography>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap" }}>
                {filterList.map((item: any) => {
                  return (
                    <div
                      key={uniqueId("CATEGORY")}
                      style={{ display: "flex", flexDirection: "column", maxWidth: "200px" }}
                    >
                      <Typography showBold color={tokens.Text.General.Secondary} variant={"label"}>
                        {item?.name}
                      </Typography>
                      {item?.options?.length ? (
                        item?.options?.slice(0, getSliceNoToShowRecord(item)).map((item2: any, index: number) => {
                          return (
                            <React.Fragment key={uniqueId("OPTIONS_")}>
                              <Typography className="ellipse-one" variant={"label"}>
                                {item2}
                              </Typography>
                              {index === 9 &&
                                getSliceNoToShowRecord(item) < item?.options?.length &&
                                getMoreOrLessButton("See more", item, false)}
                              {index > 9 &&
                                index === item?.options?.length - 1 &&
                                getSliceNoToShowRecord(item) === item?.options?.length &&
                                getMoreOrLessButton("See less", item, true)}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <Typography className="ellipse-one" variant={"label"}>
                          {"All"}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </AccordionSummary>
        </Accordion>
      )}
    </>
  );
};
export default GlobalFilterDescription;
